import { cn, Icon, PageHeader } from "@platform/ui";
import { useSession } from "@platform/ui/features";
import {
  BadgeCheck,
  ChevronRight,
  Landmark,
  type LucideIcon,
  PackageSearch,
  Scale,
  ShieldAlert,
} from "lucide-react";
import { Link } from "react-router";
import { useTranslations } from "use-intl";
import { useQueueCounts } from "../lib/queues";

const LATER = ["reconciliation"] as const;

function QueueCard({
  to,
  title,
  icon,
  waiting,
  overdue,
  counted = "admin.queue.waiting",
}: {
  to: string;
  title: string;
  icon: LucideIcon;
  waiting: number | undefined;
  /** Cases past the decision target, for the queues that have one (card 117). */
  overdue?: number | undefined;
  counted?: "admin.queue.waiting" | "admin.risk.matches.waiting";
}) {
  const t = useTranslations();
  const busy = waiting !== undefined && waiting > 0;
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 rounded-(--radius-card) border border-border bg-surface p-5 transition-colors duration-(--duration-fast) hover:border-brand-600"
    >
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex size-10 shrink-0 items-center justify-center rounded-[10px]",
          busy ? "bg-tone-attention-bg text-tone-attention-dot" : "bg-subtle text-ink-muted",
        )}
      >
        <Icon icon={icon} size={20} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-subheading font-semibold text-ink group-hover:text-brand-600">{title}</span>
        <span className="text-small text-ink-muted">
          {waiting === undefined || waiting === 0 ? t("admin.emptyQueue") : t(counted, { count: waiting })}
          {overdue !== undefined && overdue > 0 && (
            <span className="font-medium text-tone-attention-fg">
              {` · ${t("admin.target.overdueCount", { count: overdue })}`}
            </span>
          )}
        </span>
      </span>
      {busy && <span className="text-title font-semibold text-ink tabular-nums">{waiting}</span>}
      <Icon icon={ChevronRight} className="shrink-0 text-ink-muted" />
    </Link>
  );
}

/**
 * Work queues (ADM-2, ADM-3): verification, product review, the published products new risk-list entries matched
 * (card 076), and open disputes (card 090), for the operators who hold them. Verification and product review also
 * count their cases past the 24-hour decision target (card 117).
 */
export function DashboardPage() {
  const t = useTranslations();
  const { state } = useSession();
  const permissions = state.status === "signed_in" ? state.claims.perm : [];
  const verifies = permissions.includes("ops:verification.review");
  const reviews = permissions.includes("ops:catalog.manage");
  const risks = permissions.includes("ops:risk.manage");
  const resolves = permissions.includes("ops:dispute.resolve");
  const counts = useQueueCounts();
  const cases = counts.verification;
  const products = counts.productReviews;
  const matches = counts.riskMatches;
  const disputes = counts.disputes;

  return (
    <section aria-labelledby="dashboard-title" className="flex flex-col gap-5">
      <PageHeader
        id="dashboard-title"
        title={t("admin.dashboardTitle")}
        description={t("admin.dashboardIntro")}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {verifies && (
          <QueueCard
            to="/verification"
            title={t("admin.verificationQueue")}
            icon={BadgeCheck}
            waiting={cases}
            overdue={counts.verificationOverdue}
          />
        )}
        {reviews && (
          <QueueCard
            to="/product-reviews"
            title={t("admin.productReview")}
            icon={PackageSearch}
            waiting={products}
            overdue={counts.productReviewsOverdue}
          />
        )}
        {risks && (
          <QueueCard
            to="/risk-lists"
            title={t("admin.riskMatches")}
            icon={ShieldAlert}
            waiting={matches}
            counted="admin.risk.matches.waiting"
          />
        )}
        {resolves && <QueueCard to="/disputes" title={t("admin.disputes")} icon={Scale} waiting={disputes} />}
        {LATER.map((queue) => (
          <div
            key={queue}
            className="flex items-center gap-4 rounded-(--radius-card) border border-dashed border-border-strong p-5"
          >
            <span
              aria-hidden="true"
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-subtle text-ink-muted"
            >
              <Icon icon={Landmark} size={20} />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-subheading font-semibold text-ink-muted">{t(`admin.${queue}`)}</span>
              <span className="text-small text-ink-muted">{t("admin.reconciliationLater")}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
